<script lang="ts">
  export const PODIUM_SIZE = 3;
</script>
<script lang="ts" setup>
  import { computed } from 'vue';
  import type { SongSubmissionScore } from '../../../../shared/api/round.ts';
  import { formatSignedScore } from '../formatSignedScore.ts';

  const props = defineProps<{ songSubmissionScores: SongSubmissionScore[] }>();

  const medals = ['🥇', '🥈', '🥉'];
  const toneStepPercent = 12;

  const podium = computed(() =>
    props.songSubmissionScores
      .filter((score) => score.playerStanding.placement <= PODIUM_SIZE)
      .map((score) => ({
        score,
        medal: medals[score.playerStanding.placement - 1],
        toneMix: `${(score.playerStanding.placement - 1) * toneStepPercent}%`,
      })),
  );
</script>

<template>
  <ol class="podium">
    <li
      v-for="{ score, medal, toneMix } in podium"
      :key="score.playerStanding.player.id"
      :style="{ '--podium-tone-mix': toneMix }"
      class="podium-row"
    >
      <span class="rank">#{{ score.playerStanding.placement }}</span>
      <span class="player">
        <span class="nickname">{{ score.playerStanding.player.nickname }}</span>
        <span class="score">{{ score.playerStanding.score }} ({{ formatSignedScore(score.score) }})</span>
      </span>
      <span class="medal">{{ medal }}</span>
    </li>
  </ol>
</template>

<style scoped>
  .podium {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 0.5rem;
    list-style: none;
    margin: 0;
    max-width: var(--control-max-width);
    padding: 0;
    width: 100%;
  }

  .podium-row {
    align-items: center;
    background: color-mix(in srgb, var(--color-primary), var(--color-tint) var(--podium-tone-mix));
    border-radius: var(--radius-control);
    box-shadow: 0 4px 6px var(--color-shadow-faint);
    color: var(--color-on-primary);
    display: flex;
    gap: 1rem;
    padding: 0.5rem 1rem;
  }

  .rank {
    font-family: var(--font-display);
    font-size: var(--font-size-lg);
    min-width: 2.5rem;
  }

  .player {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  .nickname {
    font-family: var(--font-nickname);
    font-size: var(--font-size-md);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .score {
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-strong);
  }

  .medal {
    font-size: var(--font-size-xl);
  }
</style>
